import React, { Fragment, useEffect } from "react";
import clsx from "clsx";

import {
  Bar,
  Tooltip,
  ResponsiveContainer,
  YAxis,
  XAxis,
  Cell,
  ComposedChart,
} from "recharts";

import {
  selectFormat,
  CustomizedYAxisTick,
  CustomizedXAxisTickShortDate,
  StandardTooltip,
  Error,
  getAllKeys,
  MAIN_COLORS,
  SOCIALS_COLORS,
} from "./../Utils/Utils.js";

const getSocialColor = (key) => {
  return (
    SOCIALS_COLORS[key.toLowerCase()] && SOCIALS_COLORS[key.toLowerCase()][0]
  );
};

const WorldCupBarChartProgress = (props) => {
  let { chart: data, phases } = props.data;
  const shouldGroupTooltips = props.title.includes(
    "Total New Followers By Data Source"
  );

  useEffect(() => {
    props.childRef({ getExportData });
  });

  if (Object.keys(data).length <= 1) {
    return <Error minHeight={360} />;
  }

  const getExportData = () => ({
    data: data,
    opts: {
      type: "chart",
    },
  });

  const getPhaseWidths = () => {
    const phaseWidthGroupped = [];
    const wSize = 100 / data.length;
    phases.forEach((phase, id) => (phase.id = id));
    data.forEach((d) => {
      const currentPhase = phases.find((phase) => {
        return (
          new Date(d.date).getTime() >= new Date(phase.start).getTime() &&
          new Date(d.date).getTime() <= new Date(phase.end).getTime()
        );
      });

      // get the last group
      const lastGroup = phaseWidthGroupped[phaseWidthGroupped.length - 1];

      if (!lastGroup || lastGroup.name !== currentPhase?.name) {
        phaseWidthGroupped.push({
          name: currentPhase?.name,
          width: wSize,
        });
      } else {
        lastGroup.width = lastGroup.width + wSize;
      }
    });

    return phaseWidthGroupped.map((w) => ({
      ...w,
      width: w.width + "%",
    }));
  };

  const phaseWidths = getPhaseWidths(phases);

  const renderBars = (data) => {
    let bars = [];
    let all_bars = [];

    const sortedKeys = getSortedKeys(data);

    for (let i = 0; i <= data.length - 1; i++) {
      sortedKeys.forEach((source) => all_bars.push(source));
    }

    const isFixedBarWidth = data.length <= 7;

    [...new Set(all_bars)].forEach((bar, i) => {
      // exclude view_rate or engagement_rate
      if (bar !== "view_rate" || bar !== "engagement_rate")
        bars.push(
          <Bar
            key={i}
            dataKey={bar}
            stackId="stack"
            barSize={isFixedBarWidth ? 35 : null}
            fill={getSocialColor(bar) || colorKeys[bar]}
            yAxisId="left"
          >
            {data.map(
              (entry, index) =>
                entry && (
                  <Cell
                    key={`cell-${bar}-${index}`}
                    fillOpacity={entry[bar] < 0 ? 0.5 : 1}
                  />
                )
            )}
          </Bar>
        );
    });

    return bars;
  };

  const NETWORKS_ORDER = [
    "facebook",
    "instagram",
    "twitter",
    "tiktok",
    "youtube",
    "clips",
    "livestream",
  ];

  const legendElement = (data) =>
    data.map((entry, i) => (
      <span key={i} className="mt-2 legend mt-md-0">
        <span
          className="legend__icon"
          style={{ background: getSocialColor(entry) || colorKeys[entry] }}
        ></span>
        <span
          className="legend__text"
          style={{ color: getSocialColor(entry) || colorKeys[entry] }}
        >
          {entry.replace(/_/g, " ")}
        </span>
      </span>
    ));

  const renderLegends = (data) => {
    const sortedKeysByValue = getSortedKeys(data).map((key) =>
      key.toLowerCase()
    );

    // First, get networks in the order specified by NETWORKS_ORDER
    const orderedLegends = NETWORKS_ORDER.filter((network) =>
      sortedKeysByValue.includes(network.toLowerCase())
    );

    // Then, add other networks not in NETWORKS_ORDER, maintaining their value-based order
    const otherLegends = sortedKeysByValue.filter(
      (key) => !NETWORKS_ORDER.map((n) => n.toLowerCase()).includes(key)
    );

    // Combine and pass them to legendElement
    return legendElement([...orderedLegends, ...otherLegends]);
  };

  const getSortedKeys = (_data) => {
    if (!_data) return [];
    if (!_data[0]) return [];
    const data = _data.filter((d) => d);

    const keys = getAllKeys(data, ["date", "view_rate", "engagement_rate"]);

    const len = data.length;

    return keys
      .map((key) => ({
        key: key,
        value: data.reduce((accum, curr) => accum + (curr[key] || 0), 0) / len,
      }))
      .sort((a, b) => b.value - a.value)
      .map((d) => d.key);
  };

  const renderPhaseTitles = () => (
    <div className="d-flex phase-titles-bg">
      {phaseWidths.map((el, i) => {
        const widthNumber = parseFloat(el.width.replace("%", ""));
        return (
          <div
            key={i}
            style={{ width: el.width }}
            className={clsx("phase-titles-bg__item", { "no-border": !el.name })}
          >
            <div
              style={{
                writingMode: widthNumber < 5 ? "vertical-rl" : "horizontal-tb",
              }}
            >
              {el.name}
            </div>
          </div>
        );
      })}
    </div>
  );

  const getPhaseStyles = (el, i) => ({
    width: el.width,
    backgroundColor: i % 2 === 0 ? "#FEF0EE" : "",
  });

  const formatTooltipValue = (key, data) => {
    let formatted_value = "";
    switch (key) {
      case "view_rate":
      case "engagement_rate":
        formatted_value = selectFormat("percentage", data[key]);
        break;
      default:
        formatted_value = selectFormat("number", data[key]);
    }

    return formatted_value;
  };

  let colorKeys = getSortedKeys(data).reduce(
    (prev, next, i) => ({ ...prev, [next]: MAIN_COLORS[i] }),
    {}
  );

  const formatKey = (key, replacements = []) => {
    let formattedKey = key.replace(/_/g, " ");

    if (shouldGroupTooltips) {
      replacements.forEach((k) => {
        formattedKey = formattedKey.replace(k, "");
      });
    }

    return formattedKey;
  };

  const renderChart = (data) => {
    const interval = data.length > 200 ? 20 : 5;
    return (
      <>
        {renderLegends(data)}

        <div className="composed-chart position-relative mt-3">
          {/*  Phase Background */}
          <div className="composed-chart__phase_bg">
            {phaseWidths.map((el, i) => (
              <div
                key={i}
                className="composed-chart__phase_bg--item"
                style={getPhaseStyles(el, i)}
              />
            ))}
          </div>
          <ResponsiveContainer height={350} width="100%">
            <ComposedChart
              data={data}
              margin={{ top: 12, right: 0, bottom: 0, left: -38 }}
              stackOffset="sign"
            >
              <Tooltip
                cursor={{ fill: "#eee", opacity: 0.5 }}
                content={
                  <StandardTooltip
                    render={({ data }) => {
                      const payload = data(props);

                      if (payload !== null) {
                        const tooltip_data = payload[0].payload;
                        let total = "";

                        if (tooltip_data.total) {
                          total = tooltip_data.total;
                        } else {
                          total = payload.reduce(
                            (acc, cur) => acc + (cur.value || 0),
                            0
                          );
                        }

                        const groups = [];
                        if (!shouldGroupTooltips) {
                          groups.push(tooltip_data);
                        } else {
                          const deportes = {
                            groupName: "Telemundo Deportes",
                          };

                          const telemundo = {
                            groupName: "Telemundo",
                          };

                          Object.keys(tooltip_data).forEach((key) => {
                            if (key.toLocaleLowerCase().includes("deportes")) {
                              deportes[key] = tooltip_data[key];
                            } else {
                              telemundo[key] = tooltip_data[key];
                            }
                          });

                          groups.push(telemundo, deportes);
                        }
                        return (
                          <div className="standard-tooltip">
                            {tooltip_data.date && (
                              <header className="standard-tooltip__header">
                                <span className="standard-tooltip__key-header">
                                  Date:
                                </span>
                                <span className="standard-tooltip__value-header">
                                  {selectFormat("date", tooltip_data.date)}
                                </span>
                              </header>
                            )}
                            <main className="standard-tooltip__main">
                              {groups.map((dataGroup, i) => {
                                let totalGroup = 0;
                                Object.entries(dataGroup).forEach(
                                  ([key, value]) => {
                                    if (!["groupName", "date"].includes(key)) {
                                      totalGroup += value;
                                    }
                                  }
                                );
                                return (
                                  <Fragment key={i}>
                                    {shouldGroupTooltips && (
                                      <div
                                        className="standard-tooltip__item"
                                        style={{
                                          marginLeft: "-15px",
                                        }}
                                      >
                                        <span className="standard-tooltip__key text-capitalize total">
                                          {dataGroup.groupName}{" "}
                                        </span>
                                        <span className="standard-tooltip__value">
                                          {selectFormat("number", totalGroup)}
                                        </span>
                                      </div>
                                    )}

                                    {Object.keys(dataGroup)
                                      .filter(
                                        (key) =>
                                          !["date", "groupName"].includes(key)
                                      )
                                      .sort()
                                      .map((key, index) => (
                                        <div
                                          className="standard-tooltip__item"
                                          key={key}
                                          style={{
                                            marginLeft: !shouldGroupTooltips
                                              ? 0
                                              : "15px",
                                          }}
                                        >
                                          <span
                                            className="standard-tooltip__icon"
                                            style={{
                                              background:
                                                getSocialColor(key) ||
                                                colorKeys[key],
                                            }}
                                          ></span>
                                          <span
                                            className="standard-tooltip__key text-capitalize text-wrap"
                                            style={{
                                              wordBreak: "keep-all",
                                              color:
                                                getSocialColor(key) ||
                                                colorKeys[key],
                                            }}
                                          >
                                            {formatKey(key, [
                                              "Telemundo",
                                              "Deportes",
                                            ])}
                                          </span>
                                          <span className="standard-tooltip__value">
                                            {formatTooltipValue(key, dataGroup)}
                                          </span>
                                        </div>
                                      ))}
                                  </Fragment>
                                );
                              })}
                              <div className="standard-tooltip__dilimiter1"></div>
                              <div className="standard-tooltip__item">
                                <span className="standard-tooltip__key text-capitalize total">
                                  Total{" "}
                                </span>
                                <span className="standard-tooltip__value">
                                  {selectFormat("number", total)}
                                </span>
                              </div>
                            </main>
                          </div>
                        );
                      }
                    }}
                  />
                }
              />

              {renderBars(data)}

              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                interval={interval}
                tick={<CustomizedXAxisTickShortDate />}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tickFormatter={(d) => selectFormat("number", d)}
                tick={<CustomizedYAxisTick />}
                yAxisId="left"
                orientation="left"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {renderPhaseTitles()}
      </>
    );
  };

  return renderChart(data);
};

export default WorldCupBarChartProgress;
